"use client";

import { c as _c } from "react/compiler-runtime";
import useCreateModalState from "@/hooks/useCreateModalState";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createBreakpoint } from "react-use";
import { getActiveWaveIdFromUrl } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import type { ApiDrop } from "../../generated/models/ApiDrop";
import { DropSize } from "../../helpers/waves/drop.helpers";
import { useSidebarState } from "../../hooks/useSidebarState";
import { useAuth } from "../auth/Auth";
import BrainDesktopDrop from "../brain/BrainDesktopDrop";
import WebBrainLeftSidebar from "../brain/left-sidebar/web/WebLeftSidebar";
import { useLayout } from "../brain/my-stream/layout/LayoutContext";
import BrainRightSidebar, { SidebarTab } from "../brain/right-sidebar/BrainRightSidebar";
import CreateWaveModal from "../waves/create-wave/CreateWaveModal";
import { WaveChatScrollProvider } from "@/contexts/wave/WaveChatScrollContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
import { DROP_DETAIL_STALE_TIME_MS, fetchDropByIdBatched, getDropQueryKey } from "@/services/api/drop-api";
const useBreakpoint = createBreakpoint({
  XL: 1400,
  LG: 1024,
  S: 0
});
interface WavesMessagesWrapperProps {
  readonly children: ReactNode;
  readonly defaultPath?: string | undefined; // "/waves" or "/messages"
  readonly showLeftSidebar?: boolean | undefined;
}
const WavesMessagesWrapper: React.FC<WavesMessagesWrapperProps> = t0 => {
  const $ = _c(58);
  const {
    children,
    defaultPath: t1,
    showLeftSidebar: t2
  } = t0;
  const defaultPath = t1 === undefined ? "/waves" : t1;
  const showLeftSidebar = t2 === undefined ? true : t2;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const breakpoint = useBreakpoint();
  const {
    contentContainerStyle
  } = useLayout();
  const {
    isRightSidebarOpen,
    closeRightSidebar
  } = useSidebarState();
  const {
    connectedProfile
  } = useAuth();
  const {
    isWaveModalOpen,
    close
  } = useCreateModalState();
  const [sidebarTab, setSidebarTab] = useState(SidebarTab.ABOUT);
  let t3;
  let waveId;
  if ($[0] !== pathname || $[1] !== searchParams) {
    const rawDropId = searchParams.get("drop") ?? undefined;
    waveId = getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? undefined;
    t3 = rawDropId && /^[a-zA-Z0-9-_]+$/.test(rawDropId) ? rawDropId : undefined;
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t3;
    $[3] = waveId;
  } else {
    t3 = $[2];
    waveId = $[3];
  }
  const dropId = t3;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  const isMobile = breakpoint === "S";
  const isLargeDesktop = breakpoint === "XL";
  let t4;
  let t5;
  if ($[4] !== closeRightSidebar || $[5] !== isRightSidebarOpen || $[6] !== waveId) {
    t4 = () => {
      if (!waveId && isRightSidebarOpen) {
        closeRightSidebar();
      }
    };
    t5 = [waveId, isRightSidebarOpen, closeRightSidebar];
    $[4] = closeRightSidebar;
    $[5] = isRightSidebarOpen;
    $[6] = waveId;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== effectiveDropId) {
    t6 = getDropQueryKey(effectiveDropId);
    $[9] = effectiveDropId;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== effectiveDropId) {
    t7 = () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return fetchDropByIdBatched(effectiveDropId);
    };
    $[11] = effectiveDropId;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = !!effectiveDropId;
  let t9;
  if ($[13] !== t6 || $[14] !== t7 || $[15] !== t8) {
    t9 = {
      queryKey: t6,
      queryFn: t7,
      placeholderData: keepPreviousData,
      enabled: t8,
      staleTime: DROP_DETAIL_STALE_TIME_MS
    };
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    data: drop,
    error: dropError
  } = useQuery(t9);
  let t10;
  if ($[17] !== beginClosingDrop || $[18] !== defaultPath || $[19] !== dropId || $[20] !== pathname || $[21] !== router || $[22] !== searchParams) {
    t10 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
        markDropCloseNavigation();
      }
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("drop");
      const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname || defaultPath;
      router.replace(newUrl, {
        scroll: false
      });
    };
    $[17] = beginClosingDrop;
    $[18] = defaultPath;
    $[19] = dropId;
    $[20] = pathname;
    $[21] = router;
    $[22] = searchParams;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const onDropClose = t10;
  drop?.id;
  let t11;
  if ($[24] !== drop?.id || $[25] !== effectiveDropId) {
    t11 = effectiveDropId && drop?.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[24] = drop?.id;
    $[25] = effectiveDropId;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const isDropOpen = Boolean(t11);
  const hasWave = waveId !== undefined;
  const canShowMainContent = !isMobile || hasWave;
  const showProfileFeedShortcut = !isMobile;
  const shouldShowLeftSidebar = showLeftSidebar && (!isMobile || !hasWave && !canShowMainContent);
  const shouldShowMainContent = canShowMainContent;
  const shouldShowDropOverlay = isDropOpen && drop !== undefined && shouldShowMainContent;
  const shouldShowRightSidebar = Boolean(isRightSidebarOpen && waveId && !isDropOpen);
  const canInlineRight = !isMobile && (isLargeDesktop || breakpoint === "LG");
  let rightVariant = null;
  if (shouldShowRightSidebar) {
    rightVariant = canInlineRight ? "inline" : "overlay";
  }
  if (dropError && effectiveDropId) {
    console.error("Failed to load drop:", dropError);
  }
  let t12;
  if ($[27] !== rightVariant || $[28] !== shouldShowLeftSidebar || $[29] !== showProfileFeedShortcut) {
    t12 = shouldShowLeftSidebar && <WebBrainLeftSidebar isCollapsed={rightVariant === "inline"} showProfileFeedShortcut={showProfileFeedShortcut} />;
    $[27] = rightVariant;
    $[28] = shouldShowLeftSidebar;
    $[29] = showProfileFeedShortcut;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== children || $[32] !== drop || $[33] !== onDropClose || $[34] !== shouldShowDropOverlay || $[35] !== shouldShowMainContent) {
    t13 = shouldShowMainContent && <div className="tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-grow tw-flex-col tw-border-y-0 tw-border-l-0 tw-border-r tw-border-solid tw-border-iron-800">{children}{shouldShowDropOverlay && <div className="tw-fixed tw-inset-y-0 tw-left-[var(--left-rail,0px)] tw-right-0 tw-z-[1010] lg:tw-absolute lg:tw-inset-0 lg:tw-z-[1010]"><BrainDesktopDrop drop={{
          type: DropSize.FULL,
          ...drop,
          stableKey: drop.id,
          stableHash: drop.id
        }} onClose={onDropClose} /></div>}</div>;
    $[31] = children;
    $[32] = drop;
    $[33] = onDropClose;
    $[34] = shouldShowDropOverlay;
    $[35] = shouldShowMainContent;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== rightVariant || $[38] !== sidebarTab || $[39] !== waveId) {
    t14 = rightVariant === "inline" && <div className="tw-hidden tw-flex-shrink-0 tw-pl-6 tw-pt-2 lg:tw-block"><BrainRightSidebar variant="inline" waveId={waveId} activeTab={sidebarTab} setActiveTab={setSidebarTab} /></div>;
    $[37] = rightVariant;
    $[38] = sidebarTab;
    $[39] = waveId;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== contentContainerStyle || $[42] !== t12 || $[43] !== t13 || $[44] !== t14) {
    t15 = <div className="tw-relative tw-flex tw-min-h-0 tw-flex-col"><div className="tw-relative tw-flex tw-min-h-0 tw-flex-grow"><div className="tw-relative tw-mx-auto tw-flex tw-min-h-0 tw-w-full tw-max-w-full tw-flex-grow"><div className="tw-relative tw-flex tw-min-h-0 tw-w-full tw-overflow-hidden" style={contentContainerStyle}>{t12}{t13}{t14}</div></div></div></div>;
    $[41] = contentContainerStyle;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== rightVariant || $[47] !== sidebarTab || $[48] !== waveId) {
    t16 = rightVariant === "overlay" && <BrainRightSidebar variant="overlay" waveId={waveId} activeTab={sidebarTab} setActiveTab={setSidebarTab} />;
    $[46] = rightVariant;
    $[47] = sidebarTab;
    $[48] = waveId;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== close || $[51] !== connectedProfile || $[52] !== isWaveModalOpen) {
    t17 = connectedProfile && <CreateWaveModal isOpen={isWaveModalOpen} onClose={close} profile={connectedProfile} />;
    $[50] = close;
    $[51] = connectedProfile;
    $[52] = isWaveModalOpen;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== t15 || $[55] !== t16 || $[56] !== t17) {
    t18 = <WaveChatScrollProvider>{t15}{t16}{t17}</WaveChatScrollProvider>;
    $[54] = t15;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  return t18;
};
export default WavesMessagesWrapper;